import React from "react";
import notification from '../../../systems/notification';
import {t} from '../../../systems/i18n/index.js';
import lodash from 'lodash';
import {trans} from "../../../systems/i18n/index";

/**
 * Số dòng lỗi tối đa hiển thị trong 1 thông báo, tránh tràn màn hình với file lớn
 */
const MAX_ERROR_LINES = 10;

/**
 * Các nhóm lỗi theo dòng do ValidateManualFileData ở backend trả về,
 * xếp theo thứ tự ưu tiên hiển thị
 */
const ROW_ERROR_KEYS = [
    'not_exists',
    'required',
    'invoice_code_invalid',
    'account_invalid',
    'amount_invalid',
    'service_fee_invalid',
    'transaction_time_invalid',
];

export function* uploadFileSuccess(action) {
    let filename = <span className="a-text--break-all">{lodash.get(action, 'payload.file.name', '')}</span>;
    yield notification.success(<span>{trans('transaction:message.upload_success', {filename: filename})}</span>);
}

export function* uploadFileFailed(action) {
    const filename = lodash.get(action, 'payload.file.name', '');
    const data = lodash.get(action, 'payload.file.response.data', {});

    if (isPermissionDenied(action)) {
        return yield notification.error(t('error_page.permission_not_allow', {filename}));
    }

    const rowErrors = collectRowErrors(data);
    if (rowErrors.length) {
        return yield notification.error(renderRowErrors(filename, rowErrors));
    }

    if (lodash.get(data, 'file', '') === "file is edited") {
        return yield notification.error(t('transaction:message.upload_failed_edited', {filename}));
    }

    if (lodash.get(data, 'filename', '') === "INVALID") {
        return yield notification.error(t('transaction:message.upload_failed_filename', {filename}));
    }

    if (lodash.get(data, 'extension')) {
        return yield notification.error(t('transaction:message.upload_failed_extension'));
    }

    if (lodash.get(data, 'file_structure')) {
        return yield notification.error(t('transaction:message.upload_failed_structure', {filename}));
    }

    if (lodash.get(data, 'rows')) {
        return yield notification.error(t('transaction:message.upload_failed_empty', {filename}));
    }

    // account_invalid dạng chuỗi là contract cũ của các loại file alipay
    const account = lodash.get(data, 'account_invalid', "");
    if (lodash.isString(account) && account) {
        return yield notification.error(t('transaction:message.upload_failed_account', {account}));
    }

    return yield notification.error(t('transaction:message.upload_failed', {filename}));
}

/**
 * Chỉ báo lỗi phân quyền khi thực sự bị middleware chặn (403),
 * không dùng làm thông báo mặc định cho mọi lỗi upload
 *
 * @param action
 * @return {boolean}
 */
function isPermissionDenied(action) {
    return lodash.get(action, 'payload.file.error.status', 0) === 403
        || lodash.get(action, 'payload.file.response.code', 0) === 403;
}

/**
 * Gom các lỗi theo dòng thành danh sách câu thông báo đọc được
 *
 * @param data phần data của response 422
 * @return {Array}
 */
function collectRowErrors(data) {
    let messages = [];

    ROW_ERROR_KEYS.forEach(key => {
        let rows = lodash.get(data, key);
        if (!rows) {
            return;
        }

        // backend trả về mảng, nhưng vẫn nhận cả object đơn cho chắc
        if (!lodash.isArray(rows)) {
            rows = [rows];
        }

        rows.forEach(row => {
            if (lodash.isPlainObject(row)) {
                messages.push(formatRowError(key, row));
            }
        });
    });

    return messages;
}

/**
 * @param {string} key tên nhóm lỗi
 * @param {object} row {line, error_data, invoice_code}
 * @return {string}
 */
function formatRowError(key, row) {
    return t('transaction:message.row_error.' + key, {
        line: lodash.get(row, 'line', ''),
        value: formatErrorValue(key, row),
    });
}

/**
 * @param {string} key
 * @param {object} row
 * @return {string}
 */
function formatErrorValue(key, row) {
    if (key === 'invoice_code_invalid') {
        return lodash.get(row, 'invoice_code', '');
    }

    const value = lodash.get(row, 'error_data', '');

    // required trả về dạng {invoice_code: true, account: true}
    if (lodash.isPlainObject(value)) {
        return lodash.keys(value).map(translateColumn).join(', ');
    }

    // not_exists trả về tên cột bị thiếu
    if (key === 'not_exists') {
        return translateColumn(value);
    }

    return lodash.isArray(value) ? value.join(', ') : value;
}

/**
 * Đổi tên cột kỹ thuật sang tên nhân viên nhìn thấy trong file
 *
 * @param {string} column
 * @return {string}
 */
function translateColumn(column) {
    return t('transaction:message.row_error_column.' + column, {defaultValue: column});
}

/**
 * @param {string} filename
 * @param {Array} messages
 * @return {*}
 */
function renderRowErrors(filename, messages) {
    const shown = messages.slice(0, MAX_ERROR_LINES);
    const remain = messages.length - shown.length;

    return (
        <div>
            <div className="a-text--break-all">
                {t('transaction:message.upload_failed_invalid_rows', {filename})}
            </div>
            <ul className="mt-2 mb-0">
                {shown.map((message, index) => <li key={index}>{message}</li>)}
            </ul>
            {remain > 0 && <div>{t('transaction:message.upload_failed_more_rows', {remain})}</div>}
        </div>
    );
}
